import { AlertTriangle, Circle, CircleCheck, CircleMinus } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";

import { ListCard } from "@/components/ui/list-card";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";

import type { ClassicTopicStatus, Draft } from "../../../api/types/draft";

import { topicFiles } from "./classicModel";
import { ResearchTopicRows } from "./ResearchTopicRows";

const FINISHED: ClassicTopicStatus[] = ["ready", "empty", "failed"];
/** Topics this run doesn't research: missing an answer, or switched off. */
const NOT_RUN: ClassicTopicStatus[] = ["blocked", "omitted"];

const elapsed = (since: number, now: number): string => {
  const s = Math.max(0, Math.round((now - since) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const topicIcon = (status: ClassicTopicStatus): ReactNode => {
  switch (status) {
    case "running":
      return <Spinner className="size-3.5 text-fg-3" />;
    case "ready":
      return <CircleCheck className="size-3.5 text-fg-3" />;
    case "failed":
      return <AlertTriangle className="size-3.5 text-warn" />;
    case "blocked":
    case "omitted":
    case "empty":
      return <CircleMinus className="size-3.5 text-fg-4" />;
    default:
      return <Circle className="size-3.5 text-fg-4" />;
  }
};

/** The status a heading shows for its files: the most pressing one. */
const headingStatus = (statuses: ClassicTopicStatus[]): ClassicTopicStatus => {
  const order: ClassicTopicStatus[] = [
    "running",
    "failed",
    "waiting",
    "queued",
    "ready",
    "empty",
    "omitted",
    "blocked"
  ];
  return order.find((s) => statuses.includes(s)) ?? "queued";
};

const topicLabel: Record<ClassicTopicStatus, string> = {
  ready: "Ready",
  queued: "Queued",
  waiting: "Waiting",
  running: "Running",
  empty: "No Results",
  failed: "Didn't Finish",
  blocked: "Skipped",
  omitted: "Omitted"
};

const StageRow: FC<{
  title: string;
  meta: string;
  state: "active" | "done" | "next";
}> = ({ title, meta, state }) => (
  <div className="flex items-center gap-2">
    <span className="flex w-3.5 text-fg-3">
      {state === "active" ? (
        <Spinner className="size-3.5" />
      ) : state === "done" ? (
        <CircleCheck className="size-3.5" />
      ) : (
        <Circle className="size-3.5 text-fg-4" />
      )}
    </span>
    <span
      className={
        state === "next"
          ? "text-base font-medium text-fg-3"
          : "text-base font-medium text-fg"
      }
    >
      {title}
    </span>
    <span className="text-sm text-fg-3">{meta}</span>
  </div>
);

/**
 * A draft Classic is writing: research first (four topics at a time), then
 * the one-pass writer. Driven by the listed draft's `classic_progress`; the
 * editor takes over when the document lands.
 */
export const ClassicRunPane: FC<{ draft: Draft | undefined }> = ({ draft }) => {
  const progress = draft?.classic_progress ?? null;
  const startedAt = progress?.started_at
    ? Date.parse(progress.started_at)
    : NaN;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const writing = progress?.stage === "writing";
  const topics = Object.entries(progress?.topics ?? {});
  const live = topics.filter(([, t]) => !NOT_RUN.includes(t.status));
  const finished = live.filter(([, t]) => FINISHED.includes(t.status)).length;
  const failed = live.filter(([, t]) => t.status === "failed").length;
  const detail = !progress
    ? "Starting"
    : writing
    ? "Writing from your answers and research"
    : live.length
    ? `Researching ${finished} of ${live.length} topics`
    : "Preparing";

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="classic-run">
      <div className="flex items-center gap-2 border-b border-line-1 bg-info-soft px-3 py-1.5 text-sm text-fg-2">
        <Spinner className="size-3.5 shrink-0 text-info" />
        <span className="min-w-0 flex-1 truncate">
          <span className="text-fg">Writing Classic Draft</span> · {detail}
        </span>
        {Number.isNaN(startedAt) ? null : (
          <span className="text-fg-3 tabular-nums">
            {elapsed(startedAt, now)}
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto flex max-w-140 flex-col gap-5 px-6 pt-7 pb-10">
          <div className="flex flex-col gap-3">
            <StageRow
              title="Research"
              meta={
                writing
                  ? `${finished - failed} of ${live.length} done${
                      failed ? ` · ${failed} didn't finish` : ""
                    }`
                  : "4 at a time"
              }
              state={writing ? "done" : "active"}
            />
            {topics.length ? (
              <ListCard>
                <ResearchTopicRows
                  rows={topics.map(([, t]) => t)}
                  topic={(t) => ({
                    media: topicIcon(t.status),
                    description:
                      t.status === "failed"
                        ? "Left out of the draft"
                        : t.status === "blocked"
                        ? t.reason ?? undefined
                        : topicFiles(t),
                    control:
                      t.status === "failed" ? (
                        <StatusBadge tone="warn">
                          {topicLabel.failed}
                        </StatusBadge>
                      ) : (
                        <span className="text-sm text-fg-3">
                          {topicLabel[t.status]}
                        </span>
                      )
                  })}
                  heading={(group) => {
                    const statuses = group.map((t) => t.status);
                    const runs = statuses.filter((s) => !NOT_RUN.includes(s));
                    const done = runs.filter((s) => FINISHED.includes(s));
                    const failedHere = runs.filter((s) => s === "failed");
                    const skipped = statuses.filter(
                      (s) => s === "blocked"
                    ).length;
                    const omitted = statuses.filter(
                      (s) => s === "omitted"
                    ).length;
                    return {
                      media: topicIcon(headingStatus(statuses)),
                      description: [
                        `${group.length} ${
                          group.length === 1 ? "file" : "files"
                        }`,
                        omitted ? `${omitted} omitted` : "",
                        skipped ? `${skipped} skipped` : ""
                      ]
                        .filter(Boolean)
                        .join(" · "),
                      control: failedHere.length ? (
                        <StatusBadge tone="warn">
                          {failedHere.length} {topicLabel.failed}
                        </StatusBadge>
                      ) : (
                        <span className="text-sm text-fg-3">
                          {runs.length
                            ? `${done.length} of ${runs.length} Done`
                            : skipped
                            ? topicLabel.blocked
                            : topicLabel.omitted}
                        </span>
                      )
                    };
                  }}
                />
              </ListCard>
            ) : null}
          </div>
          <StageRow
            title="Write"
            meta={
              writing
                ? "One pass over the template"
                : "Starts when research is done"
            }
            state={writing ? "active" : "next"}
          />
          {writing ? (
            <div className="flex flex-col gap-2.5 rounded-xl bg-fill-1 p-4">
              <Skeleton className="h-3.5 w-2/5" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-11/12" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : null}
          <span className="text-sm text-fg-4">
            You can close this tab or keep working. The draft opens here when
            it's ready, and Casey stays free in the meantime.
          </span>
        </div>
      </div>
    </div>
  );
};
